'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useDisclosure, Card, CardBody, Spinner, Button } from "@heroui/react"
import { HiArrowRight } from 'react-icons/hi'
import { FaMailBulk } from 'react-icons/fa'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { differenceInSeconds, parseISO } from 'date-fns';

import { useChallenge } from '@/contexts/challenge-context'
import { useUser } from '@/contexts/user-context-v2'
import ErrorCard from '@/components/error-card'
import ModalConfirmation from '@/components/modal-confirmation'
import useFeed from '@/hooks/use-feed-v2'

import FeedItem from './components/feed-item'
import FeedForm from './components/feed-form'

const MemoizedFeedItem = React.memo(FeedItem);

const RedirectComponent = ({ challenge, path }) => {
  const router = useRouter()
  useEffect(() => {
    router.replace(path)
  }, [router, challenge.id])
    
  return (
    <div className='md:max-w-[960px] md:mx-auto px-4 relative'>
      <div className='h-[150px] flex justify-center items-center flex-col'>
        <ErrorCard>Redirecting...</ErrorCard>
        <Spinner color='primary' size='xl' />
      </div>
    </div>
  )
}

export default function Feed() {
  const { challenge } = useChallenge()
  const { user } = useUser()

  const [deleteItemId, setDeleteItemId] = useState('')
  const [error, setError] = useState('')
  const [minHeight, setMinHeight] = useState('')

  const scrollElement = useRef(null)
  const firstLoad = useRef(false)
  const loadingMore = useRef(false)
  const preLoadMoreHeight = useRef(0)
  const currentPage = useRef(1)
  const reloadFirstTimer = useRef(0)
  const modalActive = useRef(false)
  const lastPostRef = useRef(null)
  
  const router = useRouter()
  const modal = useDisclosure()

  // load feed
  const feedData = useFeed(user?.token, 'challenge', challenge?.id)

  // get join info
  const challengeJoinedInfo = user?.challengesJoined?.find(c => c.id === challenge?.id) 
  
  // Handle initial scroll and scroll position after loading more data
  useEffect(() => {
    let newestItem
    let itemCount = 0
    if (feedData.data?.length) {
      const lastBlock = feedData.data[0] // reverse order, so first block is the latest
      newestItem = lastBlock?.results[0] // reverse order, so first item is the latest
      itemCount = feedData.data.reduce((acc, block) => acc + (block.results?.length || 0), 0)
    }
    
    if (feedData.data?.length && !firstLoad.current && scrollElement.current) {
      scrollElement.current.scrollIntoView()
      firstLoad.current = true
    }

    if (!modalActive?.current && preLoadMoreHeight.current > 0 && scrollElement.current) {
      const newScrollHeight = document.documentElement.scrollHeight
      const diff = newScrollHeight - preLoadMoreHeight.current
      window.scrollTo({ top: diff, behavior: 'instant' })
      preLoadMoreHeight.current = 0
      loadingMore.current = false
    } else if (newestItem && lastPostRef?.current && newestItem.id !== lastPostRef.current.id && itemCount > lastPostRef.current.itemCount) {
      // only scroll when new items added to bottom
      scrollElement.current.scrollIntoView({ behavior: 'smooth' })
    }

    if (newestItem) {
      lastPostRef.current = {
        id: newestItem.id,
        itemCount
      }
    }

  }, [feedData.data])

  const handleScroll = useCallback(async (e) => {
    const scrollPos = window.scrollY
    
    const scrollTop = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    if (!modalActive?.current && scrollPos === 0 && !loadingMore.current) {
      loadingMore.current = true
      preLoadMoreHeight.current = document.documentElement.scrollHeight
      
      const nextPage = currentPage.current + 1
      currentPage.current = nextPage
      feedData.setSize(nextPage)
    } else if (scrollTop + windowHeight >= documentHeight && Date.now() - (reloadFirstTimer?.current || 0) > 20000) {
      reloadFirstTimer.current = Date.now()
      // future reload? 
    }
  }, [feedData])

  // Setup scroll event listener separately to avoid resetting firstLoad
  useEffect(() => {
    if (!minHeight) {
      const isMobileMedia = window.matchMedia('(max-width: 768px)').matches;
      const isMobileCheck = window.innerWidth <= 768 && 'ontouchstart' in window;
      setMinHeight((isMobileMedia || isMobileCheck) ? '70vh' : '88vh')
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll, minHeight])

  // keep refreshing here to ensure the join message shows up for user
  useEffect(() => {
    if (!challengeJoinedInfo) return
    if (!challengeJoinedInfo?.joined) return 
    if (differenceInSeconds(new Date(), parseISO(challengeJoinedInfo.joined)) > 30) return

    const intervalId = setInterval(() => {
      feedData.mutate() // refresh feed data every 5 seconds
    }, 5000);

    // Stop after 30 seconds
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
    }, 30 * 1000);

    // Cleanup
    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeJoinedInfo?.joined, feedData.mutate])

  const deleteFn = async (deleteId) => {
    setDeleteItemId(deleteId)
    modal.onOpen()
  }

  const remove = async () => {
    if (!deleteItemId) {
      console.log('No deleteItemId')
      setError('No item to delete')
    }
    try {
      const deleteConfig = {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user.token
        }
      }
      const res = await fetch(`${process.env.API}/feeds/posts/${challenge.id}/${deleteItemId}`, deleteConfig)
      if (!res.ok) {
        const errMsg = await res.text()
        setError(errMsg)
      }
      
      feedData.mutate(currentFeedData => {
        if (!currentFeedData || !currentFeedData.length) return currentFeedData;
        
        // Create a deep copy of the feed data
        const updatedFeed = [...currentFeedData];
        
        // Find the block containing the item to delete
        for (let i = 0; i < updatedFeed.length; i++) {
          const blockIndex = updatedFeed[i].results.findIndex(item => item.id === deleteItemId);
          if (blockIndex !== -1) {
            // Create a new results array without the deleted item
            updatedFeed[i] = {
              ...updatedFeed[i],
              results: [
                ...updatedFeed[i].results.slice(0, blockIndex),
                ...updatedFeed[i].results.slice(blockIndex + 1)
              ]
            };
            break;
          }
        }
        
        return updatedFeed;
      }, false)

      modal.onClose()
    } catch (err) {
      console.log(err)
    }
  }

  const addReactionFn = async (postId, reaction) => {
    try {
      const postConfig = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user.token
        },
        body: JSON.stringify({
          itemId: postId,
          challengeId: challenge.id,
          reaction
        })
      }
      const res = await fetch(`${process.env.API}/feeds/reaction`, postConfig)
      if (!res.ok) {
        const errMsg = await res.text()
        setError(errMsg)
      }
      await feedData.mutate()
    } catch (err) {
      console.log(err)
    }
  }

  const removeReactionFn = async (postId, reaction) => {
    try {
      const deleteConfig = {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user.token
        },
        body: JSON.stringify({
          reaction
        })
      }
      
      const res = await fetch(`${process.env.API}/feeds/reaction/${challenge.id}/${postId}`, deleteConfig)
      if (!res.ok) {
        const errMsg = await res.text()
        setError(errMsg)
      }
      await feedData.mutate()
    } catch (err) {
      console.log(err)
    }
  }

  const commentFn = async (postId, content) => {
    try {
      const postConfig = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user.token
        },
        body: JSON.stringify({
          challengeId: challenge.id,
          postId,
          content
        })
      }
      const res = await fetch(`${process.env.API}/feeds/comments`, postConfig)
      if (!res.ok) {
        const errMsg = await res.text()
        setError(errMsg)
      } else {
        // feedData.mutate() // refresh to see comment update
      }
    } catch (err) {
      console.log(err)
    }
  }

  const removeCommentFn = async (postId, commentId) => {
    try {
      const deleteConfig = {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user.token
        }
      }
      const res = await fetch(`${process.env.API}/feeds/comments/${challenge.id}/${postId}/${commentId}`, deleteConfig)
      if (!res.ok) {
        const errMsg = await res.text()
        setError(errMsg)
      }
    } catch (err) {
      console.log(err)
    }
  }

  if (feedData.error === 'Unauthorized') {
    return <RedirectComponent challenge={challenge} path={`/challenge/${challenge.id}/dashboard`} />
  }

  if (!challenge?.id) {
    return (
      <div className='md:max-w-[960px] md:mx-auto px-4 relative'>
        <div className='h-[150px] flex justify-center items-center flex-col'>
          <ErrorCard>Challenge not found.</ErrorCard>
          <Button 
            className='mt-4'
            as={Link}
            href='/challenges'
            color='primary'
          >View Challenges</Button>
        </div>
      </div>
    )
  }

  if (user && !challenge.showPublicly && !user.challengesJoined.find(item => (item.id === challenge.id && item.joined)) && user.organizationId !== challenge.group.organizationId) {
    return <RedirectComponent challenge={challenge} path={`/join/${challenge.id}`} />
  }

  return (<>
    <div className='md:max-w-[960px] md:mx-auto px-4 relative'>
      
      {error && <div className='mb-4'><ErrorCard>{error}</ErrorCard></div>}
      
      {((!feedData.data || feedData.data?.length === 0) && feedData.isLoading || (feedData.error && feedData.error !== 'Unauthorized')) && 
        <div className='h-[150px] flex justify-center items-center flex-col'>
          {feedData.error && <ErrorCard>Attempting to reconnect to Challenge Hound.</ErrorCard>}
          <Spinner color='primary' size='xl' />
        </div>
      }

      {feedData.error === 'Unauthorized' && 
        <div style={{ height: '150px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
          <ErrorCard>You are not authorized to view this feed.</ErrorCard>
        </div>
      }

      {feedData.isLoadingMore && <div className='fixed top-[6rem] left-0 w-full flex justify-center p-1 bg-chTan-200 z-30 md:ml-[170px]'>
        <div className='w-auto gap-2 flex justify-center items-center'>
          <Spinner color='primary' size='sm' />
          <span className='text-chTan-600'>Loading...</span>
        </div>
      </div>}

      <div className='flex flex-col-reverse pt-4 items-end' style={{ minHeight }}>
        <div ref={scrollElement} className='w-auto h-16' style={{ height: '5rem' }}></div>
      
        {feedData.data?.map(page => {
          return page.results?.map(item => <MemoizedFeedItem 
            key={item.id} 
            item={item}
            modalActive={modalActive}
            deleteFn={deleteFn} 
            addReactionFn={addReactionFn}
            removeReactionFn={removeReactionFn}
            commentFn={commentFn}
            removeCommentFn={removeCommentFn}
          />)
        })}

        {/* placeholder post */}
        {(Boolean(feedData.data?.length) && feedData.total === 0) && <Card 
          radius='sm' 
          shadow='none' 
          className='w-full md:w-[650px] mx-auto bg-chCard-background text-chTan-600 mb-12'
        >
          <CardBody className='min-w-[300px] md:min-w-[350px] py-3'>
            <div className='text-chTan-800 text-2xl font-bold mb-2'>The {challenge.group.type} is ready!</div>
            <div className='mb-2'>There are no participants or posts in the {challenge.group.type}. Posts and activity will appear on this screen once it gets going.</div>
            <div className='bg-chTan-200 text-chTan-600 p-4 flex items-center mt-2 mb-1 rounded-md'>
              <div className='grow'>Invite others by sharing link or sending an invite email.</div>
              <Button 
                href={`/challenge/${challenge.id}/invite`}
                as={Link}
                className='bg-chTan-300 mx-2'
                isIconOnly><FaMailBulk className='text-xl text-chTan-600' /></Button> 
            </div>
            <div className='bg-chTan-200 text-chTan-600 p-4 flex items-center mb-1 rounded-md'>
            <div className='grow'>Preview the join page, or join the {challenge.group.type} yourself.</div>
              <Button 
                href={`/join/${challenge.id}`}
                as={Link}
                className='bg-chTan-300 mx-2'
                isIconOnly><HiArrowRight className='text-xl text-chBlue-600' /></Button>
            </div>
          </CardBody>
        </Card>}
  
      </div>

      <ModalConfirmation 
        modal={modal} 
        headerText='Confirm Delete'
        bodyText='Are you sure you want to delete this post?'
        confirmBtnText='Yes, Delete Post'
        cancelBtnText='Cancel'
        fn={remove}
      />

    </div>

    {(Boolean(user?.id) && Boolean(challenge?.id) && !feedData.error) && <div className='fixed w-full md:w-[calc(100%-300px)] md:left-[300px] bottom-[0px]'>
      <FeedForm modalActive={modalActive} onSave={async (newItem) => {
        if (!newItem) {
          feedData.mutate() // happens when new activity is added
          return
        }
        
        newItem.challengeId = challenge.id // force override here (multiple ids if saving by group)
        feedData.mutate(currentFeedData => {
          if (!currentFeedData || !currentFeedData.length) return currentFeedData;
          
          // Create a deep copy of the feed data structure
          const updatedFeed = [...currentFeedData];
          
          // Add new item to the first page's results array at the beginning
          if (updatedFeed[0] && updatedFeed[0].results) {
            updatedFeed[0] = {
              ...updatedFeed[0],
              results: [newItem, ...updatedFeed[0].results]
            };
          } else {
            // Handle case where the feed structure is different than expected
            updatedFeed[0] = {
              results: [newItem],
              total: (updatedFeed[0]?.total || 0) + 1
            };
          }
          
          setTimeout(() => {
            scrollElement.current.scrollIntoView({ behavior: 'smooth' });
          }, 100)
          return updatedFeed;
        }, false)
      }} />
    </div>}
  </>);
}